import React, { useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { routes } from 'appConstants';
import {
  ButtonV4,
  Link,
} from 'components';
import { _t, openApplicationForm } from 'utils';

import banner from 'assets/img/main-v4/hero-curve.png';
import iconSend from 'assets/img/resolutions-v4/icon-send.svg';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';

import { MOSPROM_ORG, getSeeAlso } from '../shared';
import BuyerEventPlaques from '../BuyerEventPlaques';
import ServiceTextPopup from '../ServiceTextPopup';
import { ANALYTICS_DIRECTIONS, AnalyticsPopup } from '../analyticsSupport';
import {
  SUPPORT_ADVANTAGES,
  SUPPORT_ADVANTAGES_TITLE,
  SUPPORT_COUNTERPARTY,
  SUPPORT_COUNTERPARTY_HOW,
  SUPPORT_COUNTERPARTY_HOW_TITLE,
  SUPPORT_COUNTERPARTY_LEAD,
  SUPPORT_COUNTERPARTY_PROVIDER,
  SUPPORT_COUNTERPARTY_REQUIREMENTS,
  SUPPORT_COUNTERPARTY_REQUIREMENTS_TITLE,
  SUPPORT_INTRO,
  SUPPORT_LEAD,
  SUPPORT_NOTE,
  SUPPORT_POINT_ENDS,
  SUPPORT_POINTS,
} from '../supportInfo';
import {
  EDUCATION_FORMATS,
  EDUCATION_FORMATS_TITLE,
  EDUCATION_LEAD,
  EDUCATION_SCOPE,
} from '../educationInfo';
import styles from './styles.module.scss';

type ServiceSlug = 'analytics' | 'buyer' | 'support' | 'education';

type ServiceConfig = {
  title: string;
  crumb: string;
  ctaTitle: string;
};

const SERVICES: Record<ServiceSlug, ServiceConfig> = {
  analytics: {
    title: 'Аналитическая поддержка',
    crumb: 'Аналитическая поддержка',
    ctaTitle: 'Аналитическая поддержка',
  },
  buyer: {
    title: 'Байерская программа',
    crumb: 'Байерская программа',
    ctaTitle: 'Байерская программа',
  },
  support: {
    title: 'Информационно-консультационная поддержка',
    crumb: 'Информационно-консультационная поддержка',
    ctaTitle: 'Информационно-консультационная поддержка',
  },
  education: {
    title: 'Обучение',
    crumb: 'Обучение',
    ctaTitle: 'Обучение',
  },
};

const isServiceSlug = (value: string): value is ServiceSlug => (
  Object.prototype.hasOwnProperty.call(SERVICES, value)
);

const ResolutionsService = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const service = isServiceSlug(slug) ? SERVICES[slug] : null;

  const [directionPopup, setDirectionPopup] = useState<AnalyticsPopup | null>(null);

  if (!service) {
    return <Redirect to={routes.resolutions.root} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.banner} aria-hidden>
        <img src={banner} alt="" />
      </div>

      <section className={styles.head}>
        <h1 className={styles.title}>{_t(service.title)}</h1>
        <nav className={styles.crumbs} aria-label={_t('Навигация')}>
          <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
          <span className={styles.crumbLine} aria-hidden />
          <Link to={routes.resolutions.root} className={styles.crumbLink}>{_t('Экспортерам')}</Link>
          <span className={styles.crumbLine} aria-hidden />
          <span className={styles.crumbCurrent}>{_t(service.crumb)}</span>
        </nav>
      </section>

      {slug === 'education' ? (
        <section className={styles.support}>
          <p className={styles.supportText}>{_t(EDUCATION_LEAD)}</p>
          <p className={styles.supportText}>{_t(EDUCATION_SCOPE)}</p>
          <p className={styles.supportText}>{_t(EDUCATION_FORMATS_TITLE)}</p>
          <ul className={styles.supportList}>
            {EDUCATION_FORMATS.map((line) => (
              <li key={line} className={styles.supportItem}>
                <span className={styles.supportMark}>-</span>
                <span>{_t(line)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {slug === 'buyer' ? <BuyerEventPlaques /> : null}

      {slug === 'analytics' ? (
        <section className={styles.library}>
          <ul className={styles.grid}>
            {ANALYTICS_DIRECTIONS.map((item) => (
              <li key={item.title} className={styles.card}>
                <div className={styles.cardMeta}>
                  <span className={styles.cardTag}>{_t('МОСКВА')}</span>
                  <h2 className={styles.cardTitle}>{_t(item.title)}</h2>
                </div>
                <div className={styles.cardFooter}>
                  <p className={styles.cardText}>{_t(MOSPROM_ORG)}</p>
                  <button
                    type="button"
                    className={styles.detailBtn}
                    onClick={() => setDirectionPopup(item.popup || null)}
                  >
                    <span className={styles.detailLabel}>{_t('Подробнее')}</span>
                    <span className={styles.detailIcon} aria-hidden>
                      <img src={iconArrow} alt="" width={12} height={12} />
                    </span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {slug === 'support' ? (
        <section className={styles.support}>
          <p className={styles.supportText}>{_t(SUPPORT_INTRO)}</p>
          <p className={styles.supportPoint}>{_t(SUPPORT_COUNTERPARTY)}</p>
          <p className={styles.supportText}>{_t(SUPPORT_COUNTERPARTY_LEAD)}</p>
          <p className={styles.supportText}>{_t(SUPPORT_COUNTERPARTY_REQUIREMENTS_TITLE)}</p>
          <ul className={styles.supportList}>
            {SUPPORT_COUNTERPARTY_REQUIREMENTS.map((line) => (
              <li key={line} className={styles.supportItem}>
                <span className={styles.supportMark}>•</span>
                <span>{_t(line)}</span>
              </li>
            ))}
          </ul>
          <p className={styles.supportText}>{_t(SUPPORT_COUNTERPARTY_HOW_TITLE)}</p>
          <ul className={styles.supportList}>
            {SUPPORT_COUNTERPARTY_HOW.map((line) => (
              <li key={line} className={styles.supportItem}>
                <span className={styles.supportMark}>•</span>
                <span>{_t(line)}</span>
              </li>
            ))}
          </ul>
          <p className={styles.supportText}>{_t(SUPPORT_COUNTERPARTY_PROVIDER)}</p>
          <p className={styles.supportText}>{_t(SUPPORT_LEAD)}</p>
          <ul className={styles.supportList}>
            {SUPPORT_POINTS.map((point, index) => (
              <li key={point} className={styles.supportItem}>
                <span className={styles.supportMark}>•</span>
                <span>{`${_t(point)}${SUPPORT_POINT_ENDS[index] || ''}`}</span>
              </li>
            ))}
          </ul>
          <p className={styles.supportText}>{_t(SUPPORT_NOTE)}</p>
          <p className={styles.supportText}>{_t(SUPPORT_ADVANTAGES_TITLE)}</p>
          <ul className={styles.supportList}>
            {SUPPORT_ADVANTAGES.map((line) => (
              <li key={line} className={styles.supportItem}>
                <span className={styles.supportMark}>•</span>
                <span>{_t(line)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={styles.ctaBand}>
        <h2 className={styles.ctaTitle}>{_t(service.ctaTitle)}</h2>
        <ButtonV4
          label="Оставить заявку"
          variant="outline"
          tone="onDark"
          className={styles.ctaBtn}
          iconSrc={iconSend}
          iconSize={20}
          onClick={() => openApplicationForm({
            type: 'service',
            id: slug,
            title: service.title,
            path: `${routes.resolutions.service.root}/${slug}`,
          })}
        />
      </section>

      <section className={styles.seeAlso}>
        <h2 className={styles.seeTitle}>{_t('Смотрите также')}</h2>
        <div className={styles.seeGrid}>
          {getSeeAlso(
            slug === 'analytics' ? ['analytics'] : [],
          ).map((item) => (
            <div
              key={item.id}
              className={styles.seeCard}
              style={{ backgroundImage: `url(${item.image})` }}
            >
              <div className={styles.seeCardTitle}>{_t(item.title)}</div>
              <ButtonV4
                to={item.to}
                label="Перейти в раздел"
                variant="solid"
                tone="onLight"
                iconSrc={iconArrow}
              />
            </div>
          ))}
        </div>
      </section>
      <ServiceTextPopup
        item={directionPopup}
        onClose={() => setDirectionPopup(null)}
      />
    </div>
  );
};

export default ResolutionsService;
